import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from '../../../../shared/ui/composites';
import { cn } from '../../../../lib/utils';
import { reportsApi } from '../../api/reports';
import { colLabel, visibleColumnKeys } from './constants';
import {
  applyCustomColumnsToSql,
  buildJoinSql,
  suggestJoinsFromTables,
  selectExprForField,
  parseSqlSelectKeys,
  parseSqlJoinConfig,
  sqlFromJoinConfig,
  sqlUsesPeriodParams,
  WHERE_OPS,
} from '../../utils/buildJoinSql';

const AGG_OPTIONS = [
  { value: 'VALUE', label: 'مقدار' },
  { value: 'SUM', label: 'SUM' },
  { value: 'AVG', label: 'AVG' },
  { value: 'MAX', label: 'MAX' },
  { value: 'MIN', label: 'MIN' },
  { value: 'COUNT', label: 'COUNT' },
];
const ALIASES = 'abcdefghijklmnopqrstuvwxyz'.split('');
const GROUP_LABEL = {
  facts: 'عملیاتی',
  settings: 'کاتالوگ تنظیمات',
  views: 'ویوها',
  other: 'سایر',
};
const SESSION_FILTERS = [
  { key: 'year', label: 'سال (:year)', need: 'year_number' },
  { key: 'month', label: 'ماه (:month)', need: 'month_number' },
  { key: 'months', label: 'ماه‌های دوره (:months)', need: 'month_number' },
  { key: 'supervisor', label: 'سرپرست = من', need: 'supervisor_id' },
  { key: 'personnel', label: 'پرسنل = من', need: 'personnel_id' },
  { key: 'dc', label: 'DC کاربر', need: 'dc_id' },
];
const VALUELESS_OPS = new Set(['IS NULL', 'IS NOT NULL']);
const SQL_PARAM_CHIPS = [
  { key: ':year', tip: 'سال شمسی فیلتر گزارش' },
  { key: ':month', tip: 'ماه شمسی فیلتر گزارش' },
  { key: ':months', tip: 'آرایه ماه‌ها — ANY(:months)' },
  { key: ':personnel_id', tip: 'شناسه پرسنل لاگین‌شده' },
  { key: ':dc_id', tip: 'مرکز توزیع کاربر' },
  { key: ':as_of_day', tip: 'روز جاری ماه' },
  { key: ':day', tip: 'روز شمسی فیلتر روزانه' },
];

function normalizeSql(s) {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

function nextAlias(tables) {
  const used = new Set(tables.map((t) => t.alias));
  return ALIASES.find((a) => !used.has(a)) || `t${tables.length}`;
}

function groupedCatalog(tablesCatalog) {
  const groups = { facts: [], settings: [], views: [], other: [] };
  (tablesCatalog || []).forEach((t) => {
    const name = typeof t === 'string' ? t : t.name;
    if (!name) return;
    const group = (typeof t === 'object' && t.group) || (
      name.startsWith('v_settings_') ? 'settings'
        : ['sales_calculation_result', 'personnel_target', 'profile'].includes(name) ? 'facts'
          : (t.type === 'view' || name.startsWith('v_')) ? 'views'
            : 'other'
    );
    (groups[group] || groups.other).push({ name, type: t.type, group });
  });
  return groups;
}

function emptyFilters() {
  return {
    year: true,
    month: true,
    months: false,
    supervisor: false,
    personnel: false,
    dc: false,
  };
}

function columnMapFromMeta(colMeta) {
  const map = {};
  Object.entries(colMeta || {}).forEach(([alias, meta]) => {
    map[alias] = meta?.all || [];
  });
  return map;
}

function fieldsFromSelected(selected) {
  return selected.map((s) => ({
    key: s.col,
    label: colLabel(s.col),
    isMetric: !!s.isMetric,
  }));
}

/**
 * Visual query builder + editable SQL. The textarea is the query that is saved.
 */
export default function TableJoinModal({
  open,
  onClose,
  tablesCatalog = [],
  initialConfig = null,
  initialSql = '',
  tableColumns = [],
  onApply,
}) {
  const [tables, setTables] = useState([]);
  const [joins, setJoins] = useState([]);
  const [colMeta, setColMeta] = useState({});
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [whereRows, setWhereRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pickTable, setPickTable] = useState('');
  const [search, setSearch] = useState('');
  const [sqlDraft, setSqlDraft] = useState('');
  const [sqlTouched, setSqlTouched] = useState(false);
  const [hydratedFromSql, setHydratedFromSql] = useState(false);
  const sqlRef = useRef(null);
  const savedOnOpenRef = useRef('');
  const comparedOnOpenRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const cfg = initialConfig;
    const saved = String(initialSql || '').trim();
    let loadVisual = Array.isArray(cfg?.tables) && cfg.tables.length > 0;
    if (loadVisual && saved) {
      let visual = '';
      try {
        visual = sqlFromJoinConfig(cfg) || '';
      } catch {
        visual = '';
      }
      // Stale tables from an earlier visual pick must not come back if the
      // widget SQL was pasted / edited to something else.
      if (!visual || normalizeSql(visual) !== normalizeSql(saved)) {
        loadVisual = false;
      }
    }
    if (loadVisual) {
      setTables(cfg.tables);
      setJoins(cfg.joins || []);
      setSelected(cfg.selectsMeta || []);
      setFilters({ ...emptyFilters(), ...(cfg.filters || {}) });
      setWhereRows(Array.isArray(cfg.whereRows) ? cfg.whereRows : []);
      setColMeta({});
      cfg.tables.forEach((t) => {
        loadColumns(t.name, t.alias).catch(() => {});
      });
      setHydratedFromSql(false);
    } else {
      const parsed = saved ? parseSqlJoinConfig(saved) : { tables: [] };
      if (parsed.tables?.length) {
        applyParsedVisual(parsed);
      } else {
        setTables([]);
        setJoins([]);
        setSelected([]);
        setFilters(emptyFilters());
        setWhereRows([]);
        setColMeta({});
        setHydratedFromSql(false);
      }
    }
    savedOnOpenRef.current = saved;
    comparedOnOpenRef.current = false;
    setSqlDraft(saved);
    // Paste-only widgets (no matching visual tables) must keep the textarea as-is.
    // Otherwise leftover visual state from the previous widget can overwrite it.
    setSqlTouched(!!saved && !loadVisual);
    setError('');
    setPickTable('');
    setSearch('');
    // Hydrate only when the modal opens so parent re-renders don't wipe a paste in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadColumns = async (tableName, alias) => {
    const res = await reportsApi.columns(tableName);
    const data = res.data || {};
    const dimensions = visibleColumnKeys(data.dimensions || []);
    const metrics = visibleColumnKeys(data.metrics || []);
    const all = [...new Set([...dimensions, ...metrics])];
    const meta = { dimensions, metrics, all };
    setColMeta((prev) => ({ ...prev, [alias]: meta }));
    return meta;
  };

  const applyParsedVisual = (parsed) => {
    if (!parsed?.tables?.length) return;
    setTables(parsed.tables);
    setJoins(parsed.joins || []);
    setSelected(parsed.selected || []);
    setFilters({
      year: false,
      month: false,
      months: false,
      supervisor: false,
      personnel: false,
      dc: false,
      ...(parsed.filters || {}),
    });
    setWhereRows(Array.isArray(parsed.whereRows) ? parsed.whereRows : []);
    setColMeta({});
    parsed.tables.forEach((t) => {
      loadColumns(t.name, t.alias).catch(() => {});
    });
    setHydratedFromSql(true);
  };

  const touchVisual = () => {
    // Any visual edit (add table, toggle column, join, WHERE) becomes the
    // source of truth again — including after a paste that hydrated the graph.
    comparedOnOpenRef.current = true;
    setHydratedFromSql(false);
    setSqlTouched(false);
  };

  const addTable = async (name) => {
    if (!name) return;
    setBusy(true);
    setError('');
    try {
      const alias = nextAlias(tables);
      const previous = tables;
      const meta = await loadColumns(name, alias);
      setTables((prev) => [...prev, { name, alias }]);

      if (previous.length >= 1) {
        const prevMeta = { ...colMeta };
        for (const t of previous) {
          if (!prevMeta[t.alias]) {
            prevMeta[t.alias] = await loadColumns(t.name, t.alias);
          }
        }
        const { preferred } = suggestJoinsFromTables(previous, prevMeta, meta.all || []);
        const newJoins = preferred.map((p) => ({
          type: 'LEFT',
          leftAlias: p.leftAlias,
          leftCol: p.leftCol,
          rightAlias: alias,
          rightCol: p.rightCol,
        }));
        setJoins((prev) => [...prev, ...newJoins]);
      } else {
        const hasYear = (meta.all || []).includes('year_number');
        const hasMonth = (meta.all || []).includes('month_number');
        setFilters((f) => ({ ...f, year: hasYear, month: hasMonth }));
      }
      setPickTable('');
      touchVisual();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'خطا در خواندن ستون‌ها');
    } finally {
      setBusy(false);
    }
  };

  const removeTable = (alias) => {
    setTables((prev) => prev.filter((t) => t.alias !== alias));
    setJoins((prev) => prev.filter((j) => j.leftAlias !== alias && j.rightAlias !== alias));
    setSelected((prev) => prev.filter((s) => s.alias !== alias));
    setWhereRows((prev) => prev.filter((w) => w.alias !== alias));
    setColMeta((prev) => {
      const next = { ...prev };
      delete next[alias];
      return next;
    });
    touchVisual();
  };

  const addJoinPair = (rightAlias, leftAlias, leftCol, rightCol) => {
    if (!leftAlias || !rightAlias || !leftCol || !rightCol) return;
    touchVisual();
    setJoins((prev) => {
      const exists = prev.find(
        (j) =>
          j.rightAlias === rightAlias
          && j.leftAlias === leftAlias
          && j.leftCol === leftCol
          && j.rightCol === rightCol,
      );
      if (exists) return prev.filter((j) => j !== exists);
      return [
        ...prev,
        {
          type: prev.find((j) => j.rightAlias === rightAlias)?.type || 'LEFT',
          leftAlias,
          leftCol,
          rightAlias,
          rightCol,
        },
      ];
    });
  };

  const removeJoinPair = (rightAlias, leftAlias, leftCol, rightCol) => {
    touchVisual();
    setJoins((prev) =>
      prev.filter(
        (j) =>
          !(
            j.rightAlias === rightAlias
            && j.leftAlias === leftAlias
            && j.leftCol === leftCol
            && j.rightCol === rightCol
          ),
      ),
    );
  };

  const setJoinType = (rightAlias, type) => {
    touchVisual();
    setJoins((prev) =>
      prev.map((j) => (j.rightAlias === rightAlias ? { ...j, type } : j)),
    );
  };

  const toggleCol = (alias, col, defaultAsMetric) => {
    touchVisual();
    const exists = selected.find((s) => s.alias === alias && s.col === col);
    if (exists) {
      setSelected((prev) => prev.filter((s) => !(s.alias === alias && s.col === col)));
      return;
    }
    setSelected((prev) => [
      ...prev,
      defaultAsMetric
        ? { alias, col, isMetric: true, agg: 'SUM' }
        : { alias, col, isMetric: false, agg: 'VALUE' },
    ]);
  };

  const setAgg = (alias, col, agg) => {
    touchVisual();
    const isMetric = agg !== 'VALUE' && agg !== 'NONE' && !!agg;
    setSelected((prev) =>
      prev.map((s) =>
        s.alias === alias && s.col === col
          ? { ...s, agg, isMetric }
          : s,
      ),
    );
  };

  const addWhereRow = (row) => {
    if (!row?.alias || !row?.col || !row?.op) return;
    if (!VALUELESS_OPS.has(row.op) && !String(row.value || '').trim()) return;
    touchVisual();
    setWhereRows((prev) => [...prev, { ...row }]);
  };

  const removeWhereRow = (idx) => {
    touchVisual();
    setWhereRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const primaryCols = colMeta[tables[0]?.alias]?.all || [];
  const columnMap = useMemo(() => columnMapFromMeta(colMeta), [colMeta]);
  const allKnownCols = useMemo(
    () => tables.flatMap((t) => colMeta[t.alias]?.all || []),
    [tables, colMeta],
  );

  const cfgForSql = useMemo(() => {
    const selects = selected.map((s) => ({
      expr: selectExprForField(s),
      alias: s.col,
      isMetric: !!s.isMetric,
    }));
    return {
      tables,
      joins,
      selects: selects.length ? selects : undefined,
      filters,
      whereRows,
      primaryColumns: primaryCols,
      columnMap,
    };
  }, [tables, joins, selected, filters, whereRows, primaryCols, columnMap]);

  const generated = useMemo(() => {
    const hasCustom = (tableColumns || []).some((c) => c?.math || c?.template);
    if (!tables.length || (!selected.length && !hasCustom)) return { sql: '', error: '' };
    try {
      let sql = buildJoinSql({ ...cfgForSql, selects: cfgForSql.selects });
      const alias = tables[0]?.alias || 'a';
      const hasAgg = selected.some((s) => s.isMetric && s.agg && s.agg !== 'VALUE' && s.agg !== 'NONE');
      sql = applyCustomColumnsToSql(sql, tableColumns, (key) => {
        const q = `${alias}.${key}`;
        return hasAgg ? `SUM(${q})` : q;
      });
      return { sql, error: '' };
    } catch (e) {
      return { sql: '', error: e.message || 'خطا در ساخت کوئری' };
    }
  }, [cfgForSql, tables, selected, tableColumns]);

  useEffect(() => {
    if (!open || sqlTouched) return;
    if (!generated.sql) return;
    if (!comparedOnOpenRef.current) {
      comparedOnOpenRef.current = true;
      const saved = savedOnOpenRef.current;
      if (saved && normalizeSql(saved) !== normalizeSql(generated.sql)) {
        // Pasted / previously saved SQL is the source of truth — don't overwrite it
        // with a stale visual joinConfig rebuild.
        setSqlTouched(true);
        setSqlDraft(saved);
        return;
      }
    }
    setSqlDraft(generated.sql);
  }, [generated.sql, open, sqlTouched]);

  const sqlOutOfSync = sqlTouched
    && !!generated.sql
    && normalizeSql(generated.sql) !== normalizeSql(sqlDraft);

  const rewriteFromSelection = () => {
    if (!generated.sql) {
      setError(generated.error || 'ابتدا جدول و ستون انتخاب کنید');
      return;
    }
    savedOnOpenRef.current = generated.sql;
    comparedOnOpenRef.current = true;
    setSqlTouched(false);
    setHydratedFromSql(false);
    setSqlDraft(generated.sql);
    setError('');
  };

  const insertSqlToken = (token) => {
    const el = sqlRef.current;
    setSqlTouched(true);
    if (!el) {
      setSqlDraft((prev) => `${prev}${prev && !/\s$/.test(prev) ? ' ' : ''}${token}`);
      return;
    }
    const start = el.selectionStart ?? sqlDraft.length;
    const end = el.selectionEnd ?? start;
    const next = sqlDraft.slice(0, start) + token + sqlDraft.slice(end);
    setSqlDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleApply = async () => {
    setError('');
    const sql = String(sqlDraft || '').trim();
    if (!sql) {
      setError('متن SQL خالی است — از انتخاب‌ها بسازید یا کوئری را بنویسید');
      return;
    }
    setBusy(true);
    try {
      let fields = fieldsFromSelected(selected);
      let dimensions = selected.filter((s) => !s.isMetric).map((s) => s.col);
      let metrics = selected.filter((s) => s.isMetric).map((s) => s.col);
      try {
        const res = await reportsApi.discoverSql(sql);
        const data = res.data || {};
        if ((data.fields || []).length) {
          fields = data.fields;
          dimensions = data.dimensions || [];
          metrics = data.metrics || [];
        }
      } catch (discErr) {
        if (!fields.length) {
          fields = parseSqlSelectKeys(sql).map((k) => ({
            key: k,
            label: colLabel(k),
            isMetric: false,
          }));
        }
        if (!fields.length) {
          throw discErr;
        }
      }
      // The textarea is always what gets saved. Keep visual joinConfig only when
      // it still produces this exact SQL — a paste/edit must drop leftover tables.
      const keepVisual = tables.length > 0
        && !!generated.sql
        && normalizeSql(sql) === normalizeSql(generated.sql);
      onApply({
        sql,
        joinConfig: keepVisual
          ? {
            tables,
            joins,
            filters,
            whereRows,
            selectsMeta: selected,
            primaryColumns: primaryCols,
            columnMap,
          }
          : null,
        fields,
        dimensions,
        metrics,
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'خطا در اعمال کوئری');
    } finally {
      setBusy(false);
    }
  };

  const groups = groupedCatalog(tablesCatalog);
  const usedNames = new Set(tables.map((t) => t.name));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="xl" className="rtl max-h-[92vh] w-[96vw] max-w-[1440px] !flex flex-col gap-0 overflow-hidden">
        <DialogHeader className="px-4 py-2.5 border-b border-slate-100 shrink-0">
          <div>
            <DialogTitle className="text-sm">ساخت کوئری</DialogTitle>
            <p className="text-[10px] text-slate-400 mt-0.5">
              انتخاب جداول کوئری را می‌سازد؛ می‌توانید متن را هم ویرایش یا جای‌گذاری کنید. همان متن روی ویجت ذخیره می‌شود.
            </p>
          </div>
          <DialogClose asChild>
            <button type="button" className="absolute left-4 top-2.5 text-slate-400 hover:text-slate-600">✕</button>
          </DialogClose>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-0 md:max-h-[42vh] overflow-hidden shrink-0">
          <div className="overflow-y-auto px-4 py-3 space-y-3 border-b md:border-b-0 md:border-l border-slate-100 min-h-0 max-h-[36vh] md:max-h-[42vh]">
            <SectionLabel title="جداول و JOIN" hint="کلید اتصال می‌تواند از هر جدول قبلی باشد." />
            <div className="flex gap-2">
              <select
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={pickTable}
                onChange={(e) => setPickTable(e.target.value)}
              >
                <option value="">— افزودن جدول / ویو —</option>
                {['facts', 'settings', 'views', 'other'].map((g) => (
                  groups[g]?.length ? (
                    <optgroup key={g} label={GROUP_LABEL[g]}>
                      {groups[g].map((t) => (
                        <option key={t.name} value={t.name} disabled={usedNames.has(t.name)}>
                          {t.name}{t.type === 'view' ? ' (view)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ) : null
                ))}
              </select>
              <button
                type="button"
                disabled={!pickTable || busy}
                onClick={() => addTable(pickTable)}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
              >
                {busy ? '…' : '+ افزودن'}
              </button>
            </div>

            {!tables.length && (
              <p className="text-xs text-slate-400 text-center py-3">
                جدول را اضافه کنید، یا SQL را در کادر مقابل جای‌گذاری کنید.
              </p>
            )}
            {hydratedFromSql && tables.length > 0 && (
              <p className="text-[10px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 leading-relaxed">
                جداول و JOIN از متن SQL خوانده شدند. با انتخاب جدول/ستون یا افزودن JOIN، کادر کوئری از روی انتخاب‌ها بازنویسی می‌شود تا همان چیزی ذخیره گردد.
              </p>
            )}
            {sqlDraft && !sqlUsesPeriodParams(sqlDraft) && (
              <p className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 leading-relaxed">
                این کوئری از فیلتر سال/ماه/روز بالای گزارش استفاده نمی‌کند. برای اتصال، از تراشه‌های <span className="font-mono" dir="ltr">:year</span> و <span className="font-mono" dir="ltr">:month</span> در WHERE استفاده کنید.
              </p>
            )}

            <div className="space-y-2">
              {tables.map((t, idx) => {
                const meta = colMeta[t.alias] || { all: [] };
                const tableJoins = joins.filter((j) => j.rightAlias === t.alias);
                const joinType = tableJoins[0]?.type || 'LEFT';
                const previous = tables.slice(0, idx);
                const hinted = idx === 0
                  ? { all: [] }
                  : suggestJoinsFromTables(previous, colMeta, meta.all || []);
                return (
                  <div key={t.alias} className="rounded-lg border border-slate-200 bg-white p-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-indigo-600 px-1.5 py-0.5 font-mono text-[11px] font-bold text-white">
                        {t.alias}
                      </span>
                      <span className="flex-1 text-[13px] font-semibold text-slate-800 truncate" dir="ltr">
                        {t.name}
                      </span>
                      {idx === 0 ? (
                        <span className="text-[10px] text-emerald-600 font-bold">اصلی</span>
                      ) : (
                        <select
                          className="rounded border border-slate-200 px-1 py-0.5 text-[10px]"
                          value={joinType}
                          onChange={(e) => setJoinType(t.alias, e.target.value)}
                        >
                          <option value="LEFT">LEFT</option>
                          <option value="INNER">INNER</option>
                        </select>
                      )}
                      <button type="button" onClick={() => removeTable(t.alias)} className="text-rose-400 hover:text-rose-600 text-sm">✕</button>
                    </div>
                    {idx > 0 && (
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-1">
                          {hinted.all.map((p) => {
                            const on = tableJoins.some(
                              (j) =>
                                j.leftAlias === p.leftAlias
                                && j.leftCol === p.leftCol
                                && j.rightCol === p.rightCol,
                            );
                            const label = `${p.leftAlias}.${p.leftCol}`;
                            return (
                              <button
                                key={`${p.leftAlias}.${p.leftCol}-${p.rightCol}`}
                                type="button"
                                onClick={() => addJoinPair(t.alias, p.leftAlias, p.leftCol, p.rightCol)}
                                className={cn(
                                  'rounded-full px-2 py-0.5 text-[10px] font-mono border transition',
                                  on
                                    ? 'bg-amber-100 border-amber-300 text-amber-900'
                                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-amber-200',
                                )}
                              >
                                {on ? '✓ ' : ''}{label}
                              </button>
                            );
                          })}
                        </div>
                        <JoinPairEditor
                          previous={previous}
                          rightAlias={t.alias}
                          colMeta={colMeta}
                          rightCols={meta.all || []}
                          onAdd={(la, lc, rc) => addJoinPair(t.alias, la, lc, rc)}
                        />
                        {tableJoins.map((j) => (
                          <div
                            key={`${j.leftAlias}.${j.leftCol}-${j.rightCol}`}
                            className="flex items-center gap-1 text-[10px] font-mono text-slate-600"
                            dir="ltr"
                          >
                            <span className="flex-1">
                              ON {j.leftAlias}.{j.leftCol} = {j.rightAlias}.{j.rightCol}
                            </span>
                            <button
                              type="button"
                              className="text-rose-400 hover:text-rose-600"
                              onClick={() => removeJoinPair(j.rightAlias, j.leftAlias, j.leftCol, j.rightCol)}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <SectionLabel title="شرط‌ها (WHERE)" />
            <div className="flex flex-wrap gap-1">
              {SESSION_FILTERS.map((f) => {
                const available = !f.need || allKnownCols.includes(f.need) || !allKnownCols.length;
                return (
                  <button
                    key={f.key}
                    type="button"
                    disabled={!available}
                    onClick={() => {
                      touchVisual();
                      setFilters((prev) => ({ ...prev, [f.key]: !prev[f.key] }));
                    }}
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-bold border',
                      !available
                        ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400'
                        : filters[f.key]
                          ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                          : 'bg-white border-slate-200 text-slate-500',
                    )}
                  >
                    {filters[f.key] && available ? '✓ ' : ''}{f.label}
                  </button>
                );
              })}
            </div>
            {whereRows.map((row, idx) => (
              <div key={`${row.alias}-${row.col}-${idx}`} className="flex items-center gap-1 text-[11px] font-mono" dir="ltr">
                <span className="flex-1 rounded bg-slate-50 border border-slate-200 px-2 py-1 text-slate-700">
                  {row.alias}.{row.col} {row.op} {VALUELESS_OPS.has(row.op) ? '' : row.value}
                </span>
                <button type="button" className="text-rose-400" onClick={() => removeWhereRow(idx)}>✕</button>
              </div>
            ))}
            {tables.length > 0 && (
              <WhereRowEditor tables={tables} colMeta={colMeta} onAdd={addWhereRow} />
            )}
          </div>

          <div className="overflow-y-auto px-4 py-3 space-y-3 min-h-0 max-h-[36vh] md:max-h-[42vh]">
            <SectionLabel title="ستون‌ها" hint={`${selected.length} انتخاب‌شده`} />
            {tables.length > 0 && (
              <input
                className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                placeholder="جستجوی ستون…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            <div className="space-y-2 max-h-[32vh] overflow-y-auto">
              {tables.map((t) => {
                const meta = colMeta[t.alias] || { dimensions: [], metrics: [] };
                const q = search.trim().toLowerCase();
                const match = (c) => !q || c.toLowerCase().includes(q) || colLabel(c).includes(search);
                const dims = (meta.dimensions || []).filter(match);
                const mets = (meta.metrics || []).filter(match);
                const extraSelected = selected
                  .filter((s) => s.alias === t.alias && !dims.includes(s.col) && !mets.includes(s.col))
                  .map((s) => s.col);
                if (!dims.length && !mets.length && !extraSelected.length) return null;
                return (
                  <div key={t.alias} className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-600 flex items-center gap-2">
                      <span className="font-mono bg-indigo-600 text-white px-1.5 rounded">{t.alias}</span>
                      <span dir="ltr">{t.name}</span>
                    </div>
                    <div className="p-1.5 space-y-0.5">
                      {dims.map((col) => (
                        <ColumnRow
                          key={`d-${col}`}
                          col={col}
                          selected={selected.find((s) => s.alias === t.alias && s.col === col)}
                          onToggle={() => toggleCol(t.alias, col, false)}
                          onAgg={(agg) => setAgg(t.alias, col, agg)}
                        />
                      ))}
                      {mets.map((col) => (
                        <ColumnRow
                          key={`m-${col}`}
                          col={col}
                          selected={selected.find((s) => s.alias === t.alias && s.col === col)}
                          onToggle={() => toggleCol(t.alias, col, true)}
                          onAgg={(agg) => setAgg(t.alias, col, agg)}
                        />
                      ))}
                      {extraSelected.map((col) => (
                        <ColumnRow
                          key={`x-${col}`}
                          col={col}
                          selected={selected.find((s) => s.alias === t.alias && s.col === col)}
                          onToggle={() => toggleCol(t.alias, col, false)}
                          onAgg={(agg) => setAgg(t.alias, col, agg)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-[240px] flex flex-col overflow-hidden border-t border-slate-100 px-4 py-3 gap-2">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <SectionLabel
                title="SQL ویجت"
                hint={sqlTouched ? 'ویرایش دستی — اعمال، همین متن را ذخیره می‌کند' : 'همگام با انتخاب‌ها'}
              />
              <span className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold border',
                sqlTouched
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : 'bg-emerald-50 text-emerald-800 border-emerald-200',
              )}>
                {sqlTouched ? 'دستی' : 'همگام'}
              </span>
            </div>
            {sqlOutOfSync && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1.5 shrink-0">
                <p className="text-[10px] text-indigo-800 leading-relaxed">
                  انتخاب‌ها با متن کادر فرق دارند. برای ذخیرهٔ کوئری ساخته‌شده از جدول/ستون، بازنویسی کنید.
                </p>
                <button
                  type="button"
                  onClick={rewriteFromSelection}
                  disabled={!generated.sql}
                  className="shrink-0 rounded-md bg-indigo-600 px-2 py-1 text-[10px] font-bold text-white disabled:opacity-40"
                >
                  بازنویسی از انتخاب‌ها
                </button>
              </div>
            )}
            {!sqlOutOfSync && generated.sql && (
              <button
                type="button"
                onClick={rewriteFromSelection}
                disabled={!generated.sql}
                className="self-start text-[10px] text-indigo-600 hover:text-indigo-800 disabled:opacity-40"
              >
                بازنویسی از انتخاب‌ها
              </button>
            )}
            <div className="flex flex-wrap gap-1 shrink-0">
              {SQL_PARAM_CHIPS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  title={p.tip}
                  onClick={() => insertSqlToken(p.key)}
                  className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-mono text-[9px] text-amber-800"
                >
                  {p.key}
                </button>
              ))}
            </div>
            <textarea
              ref={sqlRef}
              className={cn(
                'flex-1 min-h-[220px] w-full rounded-lg bg-slate-900 text-emerald-300 text-[12px] p-3 font-mono leading-relaxed dir-ltr text-left border-2 resize-y',
                sqlOutOfSync ? 'border-amber-400' : sqlTouched ? 'border-slate-600' : 'border-emerald-500/70',
              )}
              dir="ltr"
              spellCheck={false}
              value={sqlDraft}
              onChange={(e) => {
                setSqlTouched(true);
                setSqlDraft(e.target.value);
              }}
              onPaste={(e) => {
                const text = e.clipboardData?.getData('text') || '';
                const parsed = parseSqlJoinConfig(text);
                if (parsed.tables?.length) {
                  applyParsedVisual(parsed);
                }
              }}
              placeholder={'SELECT …\nFROM …\nJOIN …\nWHERE …'}
            />
            {generated.error && !sqlTouched && (
              <p className="text-[10px] text-amber-700">{generated.error}</p>
            )}
        </div>
        </div>

        {error && <p className="px-4 text-xs text-rose-600 shrink-0">{error}</p>}
        <div className="shrink-0 border-t border-slate-100 px-4 py-2.5 flex gap-2 justify-between bg-white">
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-xs text-slate-500 hover:bg-slate-50">
            انصراف
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={busy || !String(sqlDraft || '').trim()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {busy ? '…' : 'اعمال کوئری روی ویجت'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ title, hint }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[11px] font-bold text-indigo-700">{title}</span>
      {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
    </div>
  );
}

function ColumnRow({ col, selected, onToggle, onAgg }) {
  const on = !!selected;
  const agg = selected?.agg || (selected?.isMetric ? 'SUM' : 'VALUE');
  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs border',
        on ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-transparent hover:border-slate-100',
      )}
    >
      {on && (
        <select
          className="rounded border border-indigo-300 text-[10px] font-bold max-w-[92px]"
          value={agg}
          onChange={(e) => onAgg(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          {AGG_OPTIONS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      )}
      <button type="button" className="flex-1 text-right" onClick={onToggle}>
        <span className="block whitespace-normal break-words leading-snug">{colLabel(col)}</span>
      </button>
    </div>
  );
}

function JoinPairEditor({ previous, rightAlias, colMeta, rightCols, onAdd }) {
  const [leftAlias, setLeftAlias] = useState(previous[previous.length - 1]?.alias || '');
  const leftCols = colMeta[leftAlias]?.all || [];
  const [leftCol, setLeftCol] = useState(leftCols[0] || '');
  const [rightCol, setRightCol] = useState(rightCols[0] || '');

  useEffect(() => {
    const fallback = previous[previous.length - 1]?.alias || '';
    if (!previous.some((t) => t.alias === leftAlias)) {
      setLeftAlias(fallback);
    }
  }, [previous, leftAlias]);

  useEffect(() => {
    if (!leftCols.includes(leftCol)) setLeftCol(leftCols[0] || '');
  }, [leftCols, leftCol]);

  useEffect(() => {
    if (!rightCols.includes(rightCol)) setRightCol(rightCols[0] || '');
  }, [rightCols, rightCol]);

  if (!previous.length || !rightCols.length) return null;

  return (
    <div className="flex items-center gap-1" dir="ltr">
      <select
        className="rounded border border-slate-200 px-1 py-0.5 text-[10px] font-mono"
        value={leftAlias}
        onChange={(e) => setLeftAlias(e.target.value)}
        title="جدول مبدأ"
      >
        {previous.map((t) => (
          <option key={t.alias} value={t.alias}>{t.alias}</option>
        ))}
      </select>
      <select
        className="flex-1 rounded border border-slate-200 px-1 py-0.5 text-[10px] font-mono"
        value={leftCol}
        onChange={(e) => setLeftCol(e.target.value)}
      >
        {leftCols.map((c) => (
          <option key={c} value={c}>{leftAlias}.{c}</option>
        ))}
      </select>
      <span className="text-[10px] text-slate-400">=</span>
      <select
        className="flex-1 rounded border border-slate-200 px-1 py-0.5 text-[10px] font-mono"
        value={rightCol}
        onChange={(e) => setRightCol(e.target.value)}
      >
        {rightCols.map((c) => (
          <option key={c} value={c}>{rightAlias}.{c}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onAdd(leftAlias, leftCol, rightCol)}
        className="rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[10px] text-indigo-600"
      >
        +
      </button>
    </div>
  );
}

function WhereRowEditor({ tables, colMeta, onAdd }) {
  const [alias, setAlias] = useState(tables[0]?.alias || '');
  const cols = colMeta[alias]?.all || [];
  const [col, setCol] = useState(cols[0] || '');
  const [op, setOp] = useState('=');
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!tables.some((t) => t.alias === alias)) {
      setAlias(tables[0]?.alias || '');
    }
  }, [tables, alias]);

  useEffect(() => {
    if (!cols.includes(col)) setCol(cols[0] || '');
  }, [cols, col]);

  if (!tables.length) return null;
  const needsValue = !VALUELESS_OPS.has(op);

  return (
    <div className="flex flex-wrap items-center gap-1" dir="ltr">
      <select
        className="rounded border border-slate-200 px-1 py-0.5 text-[10px] font-mono"
        value={alias}
        onChange={(e) => setAlias(e.target.value)}
      >
        {tables.map((t) => (
          <option key={t.alias} value={t.alias}>{t.alias}</option>
        ))}
      </select>
      <select
        className="flex-1 min-w-[8rem] rounded border border-slate-200 px-1 py-0.5 text-[10px] font-mono"
        value={col}
        onChange={(e) => setCol(e.target.value)}
      >
        {cols.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <select
        className="rounded border border-slate-200 px-1 py-0.5 text-[10px]"
        value={op}
        onChange={(e) => setOp(e.target.value)}
      >
        {WHERE_OPS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {needsValue && (
        <input
          className="flex-1 min-w-[6rem] rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-mono"
          placeholder=":year یا مقدار"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      )}
      <button
        type="button"
        onClick={() => {
          onAdd({ alias, col, op, value: needsValue ? value : '' });
          setValue('');
        }}
        disabled={!alias || !col || (needsValue && !String(value).trim())}
        className="rounded bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[10px] text-indigo-700 disabled:opacity-40"
      >
        + شرط
      </button>
    </div>
  );
}
